import {
  datadogRum,
  DefaultPrivacyLevel,
  RumGlobal,
  RumInitConfiguration,
} from '@datadog/browser-rum';
import { TrackingConsent } from '@datadog/browser-core';
import { addConsentListener, getConsentState } from '../oneTrust/oneTrust';

let consentListenerId: symbol | null = null;

/**
 * Init Real User Monitoring from DataDog. Currently supporting sdk version ^7.5.0
 * for a full list of configuration refer to the DataDog documentation
 * https://docs.datadoghq.com/real_user_monitoring/browser/
 *
 * This method automatically enforces user tracking and cookie consent meaning that
 * reporting might turn off/on itself depending on users preferences.
 *
 * When consent is unknown (no OneTrust cookie), the SDK initializes in deferred
 * mode — buffering events until consent is granted via OneTrustGroupsUpdated.
 *
 * @param initConfiguration initial configuration
 */
export function initializeRum(initConfiguration: RumInitConfiguration) {
  const { hasAnalyticsConsent } = getConsentState();

  const fixedConfigurations = {
    sessionSampleRate: 100,
    sessionReplaySampleRate: 0,
    defaultPrivacyLevel: DefaultPrivacyLevel.MASK,
  };

  // Initialize RUM immediately. When consent is unknown (no cookie yet), omit
  // trackingConsent so the SDK defers sending until setTrackingConsent('granted')
  // is called — this buffers events and replays them once consent is confirmed.
  datadogRum.init({
    ...initConfiguration,
    ...fixedConfigurations,
    ...(hasAnalyticsConsent ? { trackingConsent: TrackingConsent.GRANTED } : {}),
  });

  consentListenerId ??= addConsentListener(
    ({ hasAnalyticsConsent: consent }) => {
      if (consent) datadogRum.setTrackingConsent(TrackingConsent.GRANTED);
      else datadogRum.setTrackingConsent(TrackingConsent.NOT_GRANTED);
    }
  );
}

/**
 * Returns the instance of the datadogRum object.
 *
 * @returns datadogRum instance
 */
export function getDatadogRumInstance(): RumGlobal {
  return datadogRum;
}
